import Plotly from 'plotly.js-dist-min';
import moment, { Moment } from 'moment';
import Papa from 'papaparse';
import { SetRecording } from './SetRecording';
import { RawSetRecording } from './RawSetRecording';
import { SeriesTypeSelector } from './SeriesTypeSelector';
import { groupByArray } from './groupByArray';
import { colors } from './plotColors';
import { Brzycki, Epley, Landers, Lombardi, Mayhew, Oconnor, Wathen } from './oneRepMaxFormulas';

type PlotlyVisibility = boolean | 'legendonly';

let strengthJson: Array<SetRecording> = [];
let dataTableShown = false;
const plotElementId = 'myDiv';
const numberOfExercisesToPlot = 18;

const maxMethods = [new Epley(), new Brzycki(), new Landers(), new Wathen, new Lombardi(), new Oconnor(), new Mayhew()];

initialize();

window.onresize = function () {
	Plotly.Plots.resize(plotElementId);
};

async function initialize() {
	const strengthFetch = await fetch("https://raw.githubusercontent.com/bhandziuk/simple-workout-log-backups/master/output/strength.json");
	const rawStrengthData = await strengthFetch.json();
	plotStrength(rawStrengthData);
}

function showTable(selectedData: Array<SetRecording>) {
	const reps = [];
	const setNumber = [];
	const weight = [];
	const averageCalcualtedOneRepMax = [];
	const setWeight = [];
	const exercises = [];
	const dates = [];

	selectedData = selectedData
		.toSorted((a, b) => a.dateTime.diff(b.dateTime) || a.setNumber - b.setNumber);

	for (let i = 0; i < selectedData.length; i++) {
		const repsInSet = selectedData[i].repsCount;
		const weightPerRepInSet = selectedData[i].weight;
		const aveargeCalculatedOneRepMax = selectedData[i].calculatedOneRepMaxes.map(o => o.value).reduce((acc, val) => acc + val) / selectedData[i].calculatedOneRepMaxes.length;

		reps.push(repsInSet);
		weight.push(weightPerRepInSet);
		averageCalcualtedOneRepMax.push(Math.round(aveargeCalculatedOneRepMax));
		setWeight.push(repsInSet * weightPerRepInSet);
		setNumber.push(selectedData[i].setNumber);
		exercises.push(selectedData[i].exercise);
		dates.push(selectedData[i].dateTime.format('MMM D, YYYY'));
	}

	const table = <Plotly.Data>{
		type: 'table',
		columnwidth: [75, 100, 25, 25, 50, 50, 50],
		align: 'center',
		header: {
			values: ['Date', 'Exercise', 'Set', 'Reps', 'Weight (lb)', 'Set Weight (lb)', 'Calculated 1RM (lb)'],
			align: 'center',
			//line: {width: 1, color: 'rgb(50, 50, 50)'},
			fill: { color: ['#0A418D'] },
			font: { family: 'Arial', size: 14, color: 'white' }
		},
		cells: {
			values: [dates, exercises, setNumber, reps, weight, setWeight, averageCalcualtedOneRepMax],
			align: ['center', 'center'],
			line: { color: 'black', width: 1 },
			fill: { color: ['#758AA8', 'rgba(228, 222, 249, 0.65)'] },
			font: { family: 'Arial', size: 12, color: ['black'] }
		},
		domain: {
			x: [0.2, 0.8],
			y: [0, 0.4]
		}

	};
	if (dataTableShown) {
		Plotly.deleteTraces(plotElementId, -1);
	}
	Plotly.addTraces(plotElementId, table);
	dataTableShown = true;
}

function mostRecentSession(strengthJson: Array<SetRecording>) {
	const recentSession = groupByArray(strengthJson, o => o.dateTime.clone().startOf('day').format("MMM DD, YYYY"))
		.sort(function (a, b) {
			return moment(b.key, "MMM DD, YYYY").diff(moment(a.key, "MMM DD, YYYY"));
		})
		.slice(0, 1);
	return recentSession[0].values;
}

function getDefaultExercises(data: Array<SetRecording>, exercisesToPlot: Array<string>) {
	const recentExercises = groupByArray(data, o => o.exercise);
	const defaults: Array<string> = [];
	for (let re = 0; re < recentExercises.length; re++) {
		for (let e = 0; e < exercisesToPlot.length; e++) {
			if (exercisesToPlot[e] == recentExercises[re].key) {
				defaults.push(recentExercises[re].key);
			}
		}
	}
	return defaults;
}

//https://stackoverflow.com/questions/14446511/what-is-the-most-efficient-method-to-groupby-on-a-javascript-array-of-objects
function getExercisesToPlot(strengthJson: Array<SetRecording>, topNitems: number) {
	let exercisesArr = groupByArray(strengthJson, o => o.exercise);

	exercisesArr = exercisesArr.sort(function (a, b) {
		return b.values.length - a.values.length;
	});
	exercisesArr = exercisesArr.slice(0, topNitems);
	const exercises: Array<string> = [];
	for (let e = 0; e < exercisesArr.length; e++) {
		exercises.push(exercisesArr[e].key);
	}
	exercises.push('Total Weight-All Exercises');
	return exercises;
}

function addExtraData(strengthJson: Array<RawSetRecording>) {
	let setNumber = 1;
	let modifiedStrengthData: Array<SetRecording> = [];

	for (let s = 0; s < strengthJson.length; s++) {

		let thisSetNumber: number;

		if ((s + 1) < strengthJson.length && strengthJson[s].Exercise == strengthJson[s + 1].Exercise) {
			thisSetNumber = setNumber;
			setNumber++;
		}
		else if ((s + 1) == strengthJson.length) {
			thisSetNumber = setNumber;
		}
		else {
			thisSetNumber = setNumber;
			setNumber = 1;
		}

		const thisDatetime = moment(strengthJson[s].Date + ' ' + strengthJson[s].Time, "MM/DD/YY HH:mm");
		const weight = strengthJson[s].Weight;
		const reps = strengthJson[s]['# of Reps'];

		const calculatedMaxes = maxMethods.map(o => ({ method: o.name, value: Math.round(o.calculate(weight, reps)) }));

		modifiedStrengthData.push({
			setNumber: thisSetNumber,
			dateTime: thisDatetime,
			calculatedOneRepMaxes: calculatedMaxes,
			date: strengthJson[s].Date,
			exercise: strengthJson[s].Exercise,
			notes: strengthJson[s].Notes,
			repsCount: reps,
			time: strengthJson[s].Time,
			weight: weight
		});
	}
	return modifiedStrengthData;
}

function plotStrength(rawStrengthJson: Array<RawSetRecording>) {

	strengthJson = addExtraData(rawStrengthJson);

	const recentSession = mostRecentSession(strengthJson);
	const exercisesToPlot = getExercisesToPlot(strengthJson, numberOfExercisesToPlot);
	const exercisesToShowByDefault = getDefaultExercises(recentSession, exercisesToPlot);
	const plotData = [];
	const plotLayout = [];

	for (let i = 0; i < exercisesToPlot.length; i++) {
		const thisExerciseName = exercisesToPlot[i];

		const thisExercisData = strengthJson.filter(function (ex) {
			return ex.exercise == thisExerciseName;
		});
		const layout = [];
		const weightLiftedPerSet = [];
		const weightLiftedPerRep = [];
		const date: Array<any> = [];
		const averageCalcualtedMaxes = [];
		const calculatedOneRepMaxErrors = [];
		const exercise = [];
		const cellValues = [];

		for (let e = 0; e < thisExercisData.length; e++) {
			const repsInSet = thisExercisData[e].repsCount;
			const weightPerRepInSet = thisExercisData[e].weight;
			const thisDate = thisExercisData[e].dateTime.clone().startOf('day');

			const comment = thisExercisData[e].notes;

			weightLiftedPerSet.push(repsInSet * weightPerRepInSet);
			weightLiftedPerRep.push(weightPerRepInSet);
			date.push(thisDate.toDate());

			const maxes = thisExercisData[e].calculatedOneRepMaxes.map(o => o.value);
			const aveargeCalculatedOneRepMax = maxes.reduce((acc, val) => acc + val) / maxes.length;
			const calculatedOneRepMaxError = Math.max(...maxes) - Math.min(...maxes);
			averageCalcualtedMaxes.push(aveargeCalculatedOneRepMax);
			calculatedOneRepMaxErrors.push(calculatedOneRepMaxError);
			exercise.push(thisExerciseName);
		}

		const traceVisibility = (exercisesToShowByDefault.includes(thisExerciseName) ? true : 'legendonly');

		const traceWeightLifted = {
			type: 'scatter',
			x: date,
			y: weightLiftedPerSet,
			mode: 'markers',
			marker: {
				color: colors[i]
			},
			visible: traceVisibility,
			customdata: 'TotalWeight',
			name: thisExerciseName,
			legendgroup: thisExerciseName,
			transforms: [{
				type: 'aggregate',
				groups: date,
				aggregations: [
					{ target: 'y', func: 'sum', enabled: true }
				]
			}]
		};

		const traceMaxWeightLiftedPerSet = {
			type: 'scatter',
			x: date,
			y: weightLiftedPerSet,
			mode: 'markers',
			marker: {
				color: colors[i],
				symbol: 'circle-open-dot',
				size: 7
			},
			visible: traceVisibility,
			customdata: 'MaxSetWeight',
			name: thisExerciseName,
			legendgroup: thisExerciseName,
			transforms: [{
				type: 'aggregate',
				groups: date,
				aggregations: [
					{ target: 'y', func: 'max', enabled: true }
				]
			}]
		};

		const traceMaxWeight = {
			type: 'scatter',
			x: date,
			y: weightLiftedPerRep,
			yaxis: 'y2',
			mode: 'line',
			line: {
				color: colors[i],
				width: 2,
				dash: 'solid'
			},
			customdata: 'MaxWeight',
			name: thisExerciseName,
			visible: traceVisibility,
			showlegend: false,
			legendgroup: thisExerciseName,
			transforms: [{
				type: 'aggregate',
				groups: date,
				aggregations: [
					{ target: 'y', func: 'max', enabled: true }
				]
			}]
		};

		const traceCalculatedOneRepMax = {
			type: 'scatter',
			x: date,
			y: averageCalcualtedMaxes,
			error_y: {
				type: 'data',
				array: calculatedOneRepMaxErrors,
				visible: true
			},
			yaxis: 'y2',
			mode: 'line',
			line: {
				color: colors[i],
				dash: 'dashdot',
				width: 2
			},
			customdata: 'CalcualtedOneRepMax',
			name: thisExerciseName,
			visible: traceVisibility,
			showlegend: false,
			legendgroup: thisExerciseName,
			transforms: [{
				type: 'aggregate',
				groups: date,
				aggregations: [
					{ target: 'y', func: 'max', enabled: true }
				]
			}]
		};

		plotData.push(traceWeightLifted);
		plotData.push(traceMaxWeight);
		plotData.push(traceCalculatedOneRepMax);
		plotData.push(traceMaxWeightLiftedPerSet);
	}
	const totalWeightLiftedAllExercises = getTotalWeightLiftedAllExercises(strengthJson);
	const traceTotalWeightLiftedAllExercises = {
		type: 'scatter',
		x: totalWeightLiftedAllExercises.dates,
		y: totalWeightLiftedAllExercises.values,
		mode: 'markers',
		marker: {
			color: colors[exercisesToPlot.length - 1],
			symbol: '200',
			size: 7
		},
		visible: true,
		customdata: 'TotalWeight',
		name: 'Total Weight-All Exercises',
		legendgroup: 'Total Weight-All Exercises',
		transforms: [{
			type: 'aggregate',
			groups: totalWeightLiftedAllExercises.dates,
			aggregations: [
				{ target: 'y', func: 'sum', enabled: true }
			]
		}]
	};
	plotData.push(traceTotalWeightLiftedAllExercises);

	const selectorOptions = {
		buttons: [{
			step: 'month',
			stepmode: 'backward',
			count: 3,
			label: '3m'
		}, {
			step: 'month',
			stepmode: 'backward',
			count: 6,
			label: '6m'
		}, {
			step: 'year',
			stepmode: 'backward',
			count: 1,
			label: '1y'
		}, {
			step: 'year',
			stepmode: 'todate',
			count: 1,
			label: 'YTD'
		}, {
			step: 'all',
		}],
		y: 0
	};

	const layout = <Plotly.Layout>{
		//title: '<b>Iron Moved</b>',
		//titlefont: { family: 'Courier', size: 25 },
		xaxis: {
			rangeselector: selectorOptions,
			rangeslider: {
				//domain: [0.4, 0.5],
				//height: 20
				thickness: 0.025
			},
			type: 'date',
			range: [moment().subtract(1, 'years').valueOf(), moment().valueOf()],
			domain: [0, 1]
		},
		dragmode: 'select',
		//autosize: true,
		margin: {
			//l: 20,
			//r: 20,
			b: 2,
			t: 2,
			pad: 0
			//autoexpand: true
		},
		yaxis: {
			title: 'Total Weight',
			domain: [0.5, 1]
		},
		yaxis2: {
			title: 'Max Weight',
			overlaying: 'y',
			side: 'right',
			domain: [0.5, 1]
		},
		showlegend: false
		// legend: {
		// orientation: 'h',
		// xanchor:'left',
		// yanchor:'bottom',
		// x: 0,
		// y: -10
		// }
	};


	Plotly.newPlot(plotElementId, plotData, layout, { displayModeBar: false });
	showTable(recentSession);

	const myPlot = document.getElementById(plotElementId);

	(<any>myPlot).on('plotly_click', function (clickData: Plotly.Data) {
		dataPointClick(clickData);
	});

	const seriesTypeSelectorSettings = getSeriesTypeSelectorSettings();

	createSeriesTypeSelectors(seriesTypeSelectorSettings);
	createLegend(exercisesToPlot, colors, exercisesToShowByDefault);
	showAppropriateSeries();
}

function getTotalWeightLiftedAllExercises(strengthJson: Array<SetRecording>) {
	const dates = [];
	const values = [];

	for (let i = 0; i < strengthJson.length; i++) {
		const item = strengthJson[i];
		const repsInSet = item.repsCount;
		const weightPerRepInSet = item.weight;
		const thisDate = item.dateTime.clone().startOf('day');

		dates.push(thisDate.toDate());
		values.push(repsInSet * weightPerRepInSet);
	}

	return { dates: dates, values: values };
}

function getSeriesTypeSelectorSettings(): Array<SeriesTypeSelector> {
	return [{
		name: 'Calcualted One Rep Max',
		id: 'CalcualtedOneRepMax',
		visible: false,
		//icon: '<div class="horizontal-dotted-line"></div>' //'<svg xmlns="http://www.w3.org/2000/svg" height="10" width="10"><line stroke-dasharray="5, 10"/><style><![CDATA[line{ stroke: black;stroke-width: 2;}]]></style></svg>' //'--- '
		icon: '<svg width="27" height="10" version="1.1" xmlns="http://www.w3.org/2000/svg"><line stroke-dasharray="5, 5" x1="0" y1="6" x2="30" y2="6"></line></svg>'
	},
	{
		name: 'Total Weight',
		id: 'TotalWeight',
		visible: true,
		icon: '<svg xmlns="http://www.w3.org/2000/svg" height="10" width="10"><circle cx="4" cy="4" r="4" fill="black" /></svg>'
	},
	{
		name: 'Max Total Set Weight',
		id: 'MaxSetWeight',
		visible: false,
		icon: '<svg xmlns="http://www.w3.org/2000/svg" height="10" width="10"><circle cx="5" cy="5" r="4" stroke="black" stroke-width="2" fill="none" /></svg>'
	}
		,
	{
		name: 'Max Weight',
		id: 'MaxWeight',
		visible: true,
		icon: '<svg width="27" height="10" version="1.1" xmlns="http://www.w3.org/2000/svg"><line x1="0" y1="6" x2="30" y2="6" style="stroke:rgb(0, 0, 0); stroke-width: 2" ></line></svg>'
	}];
}

function dataPointClick(clickData: any) {
	const selectedExercises = [];
	const selectedDates: Array<Moment> = [];
	const allExercises = true;
	for (let d = 0; d < clickData.points.length; d++) {
		selectedExercises.push(clickData.points[d].data.legendgroup);
		selectedDates.push(moment(clickData.points[d].x));
	}

	const selectedData = filterToDatesExercises(strengthJson, selectedDates, selectedExercises, allExercises);
	// selectedData = selectedData.sort(function(a,b){
	// return new Date(a.Date + ' ' + a.Time) - new Date(b.Date + ' ' + b.Time);
	// });
	showTable(selectedData);
}

function filterToDatesExercises(strengthJson: Array<SetRecording>, datesToFilterTo: Array<Moment>, exercisesToFilterTo: Array<string>, allExercises: boolean) {
	return strengthJson.filter(
		function (st) {
			return datesToFilterTo.some(
				function (e) {
					return (moment(st.dateTime)).diff(e, 'days') == 0
				})
				&&
				((allExercises) ? allExercises : exercisesToFilterTo.includes(st.exercise));
		});
}

function parseData(url: string, callBack: (data: any) => void) {
	Papa.parse(url, {
		download: true,
		header: true,
		complete: function (results) {
			callBack(results.data);
		}
	});
}

function getLegendGroupSettings() {
	const legendGroupSettings = [];
	let data = <Array<Plotly.Data>>(<any>document.getElementById(plotElementId)).data;
	data = data.filter(e => e.type != 'table');
	const legendgroupsPre = groupByArray<string, any>(data, o => o.legendgroup)
	for (let lg = 0; lg < legendgroupsPre.length; lg++) {
		let visible: PlotlyVisibility = 'legendonly';
		for (let lgi = 0; lgi < legendgroupsPre[lg].values.length; lgi++) {
			//console.log(legendgroupsPre[lg], legendgroupsPre[lg].values[lgi], legendgroupsPre[lg].values[lgi].visible)
			if (legendgroupsPre[lg].values[lgi].visible == true) {
				visible = true;
			}
		}
		legendGroupSettings.push({
			name: legendgroupsPre[lg].key,
			visible: visible
		});
	}
	return legendGroupSettings;
}

function getLegendGroupSettingsCustom() {
	const legendGroupSettings = [];
	const data = document.getElementById('legend');
	const legendEntries = data.getElementsByTagName('div');
	for (let e = 0; e < legendEntries.length; e++) {
		if (legendEntries[e].classList.contains('selected')) {
			legendGroupSettings.push({
				name: legendEntries[e].id,
				visible: true
			});
		}
		else {
			legendGroupSettings.push({
				name: legendEntries[e].id,
				visible: 'legendonly'
			});
		}
	}

	return legendGroupSettings;
}

function getSeriesTypeSettings() {
	const seriesTypeSettings = [];
	const data = document.getElementById('seriesTypes');
	const seriesEntries = data.getElementsByTagName('div');

	const seriesType = ""
	for (let e = 0; e < seriesEntries.length; e++) {
		if (seriesEntries[e].classList.contains('selected')) {
			seriesTypeSettings.push({
				name: seriesEntries[e].id,
				visible: true
			});
		}
		else {
			seriesTypeSettings.push({
				name: seriesEntries[e].id,
				visible: 'legendonly'
			});
		}
	}
	return seriesTypeSettings;
}
function showAppropriateSeries() {

	const seriesTypeSettings = getSeriesTypeSettings()

	const legendGroupSettings = getLegendGroupSettingsCustom();

	const data = (<any>document.getElementById(plotElementId)).data;
	const layout = (<any>document.getElementById(plotElementId)).layout;

	const visibilities: Array<{ index: number, visible: PlotlyVisibility }> = [];
	const traceIndicies: Array<number> = [];

	for (let d = 0; d < data.length; d++) {

		if (data[d].type == 'scatter') {
			for (let lg = 0; lg < legendGroupSettings.length; lg++) {
				for (let st = 0; st < seriesTypeSettings.length; st++) {
					if (data[d].customdata.includes(seriesTypeSettings[st].name)
						&& legendGroupSettings[lg].name == data[d].legendgroup) {

						const visible = (legendGroupSettings[lg].visible == true && seriesTypeSettings[st].visible == true) ? true : 'legendonly';
						visibilities.push({ visible: visible, index: d });
						data[d].visible = visible;
					}
				}
			}
		}
	}
	// TODO

	// Plotly.redraw(plotElementId);
	const legendOnly = visibilities.filter(o => o.visible == 'legendonly');
	const visibleSeries = visibilities.filter(o => o.visible == true);

	Plotly.restyle(plotElementId, <any>{ visible: legendOnly.map(o => o.visible) }, legendOnly.map(o => o.index));
	Plotly.restyle(plotElementId, <any>{ visible: visibleSeries.map(o => o.visible) }, visibleSeries.map(o => o.index));
}

function toggleSelected(btn: HTMLElement) {

	if (btn.classList.contains('selected')) {
		btn.classList.remove('selected');
		btn.classList.add('notselected');
	}
	else {
		btn.classList.remove('notselected');
		btn.classList.add('selected');
	}
	showAppropriateSeries();
}

function createSeriesTypeSelectors(seriesTypeSelectors: Array<SeriesTypeSelector>) {
	const seriesTypesDiv = document.getElementById('seriesTypes');

	for (let s = 0; s < seriesTypeSelectors.length; s++) {
		const seriesEntry = document.createElement('div');
		seriesEntry.classList.add('button');
		if (seriesTypeSelectors[s].visible) {
			seriesEntry.classList.add('selected');
		}
		else {
			seriesEntry.classList.add('notselected');
		}
		seriesEntry.id = seriesTypeSelectors[s].id; //.replace(new RegExp(' ', 'g'), '');

		seriesEntry.addEventListener('click', function (event) {
			const btn = (<HTMLElement>event.currentTarget);
			toggleSelected(btn)

		});

		const seriesIcon = document.createElement('span');
		seriesIcon.innerHTML = seriesTypeSelectors[s].icon;

		const seriesText = document.createElement('span');
		seriesText.classList.add('series-caption');
		seriesText.innerHTML = seriesTypeSelectors[s].name;

		seriesEntry.appendChild(seriesIcon);
		seriesEntry.appendChild(seriesText);
		seriesTypesDiv.appendChild(seriesEntry);
	}
}

function createLegend(exercisesToPlot: Array<string>, colors: Array<string>, exercisesToShowByDefault: Array<string>) {
	const legendDiv = document.getElementById('legend');

	for (let e = 0; e < exercisesToPlot.length; e++) {
		const legendIcon = document.createElement('span');
		legendIcon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" height="10" width="10"><circle cx="4" cy="4" r="4" fill="' + colors[e] + '" /></svg>';
		//legendIcon.style.color = colors[e];
		const legendText = document.createElement('span');
		legendText.classList.add('series-caption');
		legendText.innerHTML = exercisesToPlot[e];

		const legendEntry = document.createElement('div');

		if (exercisesToShowByDefault.includes(exercisesToPlot[e])) {
			legendEntry.classList.add('selected');
		}
		else {
			legendEntry.classList.add('notselected');
		}
		legendEntry.classList.add('button');

		legendEntry.addEventListener('click', function (event) {
			const btn = (<HTMLElement>event.currentTarget);
			toggleSelected(btn)
		});
		legendEntry.appendChild(legendIcon);
		legendEntry.appendChild(legendText);
		legendEntry.id = exercisesToPlot[e];
		legendDiv.appendChild(legendEntry);
	}
}







